import { Injectable } from '@angular/core';
import { Router, type UrlTree } from '@angular/router';
import type { Observable } from 'rxjs';
import { forkJoin, map, of, take } from 'rxjs';
import { AppStateService } from '../services/app-state.service';
import { ClinicService } from '../services/clinic.service';
import { PreferencesService } from '../services/preferences.service';
import { TokenStorageService } from '../services/token-storage.service';

@Injectable({ providedIn: 'root' })
export class LiteClinicGuard {
  constructor(
    private readonly appStateService: AppStateService,
    private readonly preferencesService: PreferencesService,
    private readonly clinicService: ClinicService,
    private readonly tokenStorage: TokenStorageService,
    private readonly router: Router
  ) {}

  // Le guard ne peut pas attendre AppStateService.clinicObservable: avec
  // initialNavigation: 'enabledBlocking', AppComponent (et donc NavigationComponent
  // qui hydrate AppStateService.clinic) n'est monté qu'après la résolution du guard.
  // On charge donc les données nécessaires nous-mêmes.
  canActivate(): Observable<boolean | UrlTree> {
    const cached = this.appStateService.clinic;
    if (cached) {
      return of(this.resolveAccess(!!cached.isLite));
    }

    const userId = this.tokenStorage.getUserId();
    if (!userId || Number.isNaN(userId)) {
      return of(true);
    }

    return forkJoin({
      prefs: this.preferencesService.getPreferences().pipe(take(1)),
      clinics: this.clinicService.userClinics(userId, !this.appStateService.isScc).pipe(take(1))
    }).pipe(
      map(({ prefs, clinics }) => {
        const selected = clinics.find(c => c.id === prefs.selectedClinicId) ?? clinics[0];
        return this.resolveAccess(!!selected?.isLite);
      })
    );
  }

  private resolveAccess(isLite: boolean): boolean | UrlTree {
    return isLite ? this.router.createUrlTree(['/studies']) : true;
  }
}
